<!--
  RatePriceField.vue
  콜봇 월 정산 · 판매가 입력 한 행. 기본 요율 편집 모달(화면 명세 3)과 회사 예외 모달(화면 명세 2-1)이
  칸마다 하나씩 쓴다.

  요청: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 2-1 예외 모달 필드", "화면 명세 3 편집 모달 필드"
        칸은 요금표 v70(~/md/front/admin-콜봇-월정산-call-api-구현회신.md "2026-10-16 개정" 1·2)이다 — 월 사용료(VAT 포함 원)·
        포함 통화(분)·120분 초과 통화(1분당)·발신 건당·받은 발신 통화·회선 월 금액.
  배치(2026-10-27 목업 RatesEdit.dc.html): 이름 | 입력 | 원가(참고) 세 칸, 그 아래 한 줄 설명. 모달이 '원가 (참고)' 열 머리를
  그리므로 costText 에는 '원가' 를 붙이지 않는다(원가가 없는 칸은 '—').
  - 단위 초와 원가는 입력받지 않는다 — 원가(costText)는 회색 읽기 전용으로 오른쪽 열에 보인다.
  - 입력 뒤 단위(suffix)는 기본 '원'이고 포함 통화는 '분'이다. 정수만 받는 칸(월 사용료·포함 통화)도 반올림하지 않는다 —
    모달이 저장할 때 검증한다(validateRateCell).
  - 원가·단위를 모르는 칸(locked)은 입력을 막고 "원가 확인 뒤 입력" 툴팁을 단다.
  - 판매가가 원가보다 낮으면 "원가 미만"(저장은 막지 않는다), 같으면 "마진 0" 보조 표기.
    둘 다 0(내선)이면 붙이지 않는다.
  - mode 'default'(기본 요율): 비운 칸은 "미정"(placeholder). 미정이 없는 칸(상담사 번호 두 칸 —
    ~/md/front/admin-콜봇-상담사번호-추가요금-프론트요청.md A1 "월 금액에 '미정'은 없다")은 부모가 emptyText 를 '' 로 넘긴다.
  - mode 'override'(회사 예외): "기본 따름"(기본 선택) / "값 입력" 중 하나. "기본 따름" 옆에 적용 시작 월
    기준 기본값(defaultText)을 회색으로 보인다. 기본 따름이면 칸을 비운다(update:value null) — 값을
    넣은 칸만 예외가 된다. 따르는 중에 값을 치면 "값 입력"으로 바뀐다. 값 입력인데 비어 있으면 기본을 따른다.
  - 검증은 하지 않는다(반올림도 하지 않는다). 모달이 저장할 때 validatePrice 로 message.warning() 한다.

  v-model:value(Number|null) · v-model:follow(Boolean, override 에서만 뜻이 있다 — override 에서는 둘 다 묶는다.
  value 만 들고 follow 를 `value == null` 로 넘겨도 동작한다). hint: 행 아래 회색 한 줄 설명.
  예외 본문은 follow 가 false 이고 값이 있는 칸만 담는다(formValuesToOverrideBody 가 null 을 뺀다).
-->
<template>
  <div class="price-field" :class="{ 'is-locked': locked, 'is-following': isFollowing }">
    <label class="price-field__label" :for="inputId">
      {{ label }}<em v-if="required" class="price-field__required" aria-hidden="true">*</em>
    </label>

    <div class="price-field__body">
      <a-radio-group
        v-if="isOverride"
        class="price-field__choice"
        :value="isFollowing ? 'follow' : 'custom'"
        :aria-label="`${label} 적용 방식`"
        @change="onChoice"
      >
        <a-radio value="follow">
          기본 따름
          <span v-if="defaultText" class="price-field__default">{{ defaultText }}</span>
        </a-radio>
        <a-tooltip :title="locked ? COST_UNKNOWN_TOOLTIP : undefined">
          <a-radio value="custom" :disabled="locked">값 입력</a-radio>
        </a-tooltip>
      </a-radio-group>

      <div class="price-field__row">
        <a-tooltip :title="locked ? COST_UNKNOWN_TOOLTIP : undefined">
          <span class="price-field__input" :class="{ 'is-disabled': locked }">
            <a-input-number
              :id="inputId"
              ref="inputRef"
              :value="shownValue"
              :min="0"
              :disabled="locked"
              :placeholder="placeholder"
              :addon-before="unitText || undefined"
              :addon-after="suffix"
              :step="step"
              @update:value="onInput"
            />
          </span>
        </a-tooltip>
        <span v-if="relationLabel" class="price-field__pill" :class="`is-${relation}`">{{ relationLabel }}</span>
      </div>
    </div>

    <div class="price-field__cost">
      <span v-if="locked && !costText" class="price-field__pill is-pending">원가 확인 중</span>
      <template v-else>{{ costText || EMPTY }}</template>
    </div>

    <p v-if="helpText" class="price-field__hint">{{ helpText }}</p>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, ref } from 'vue';

import { EMPTY } from '../service/settlementFormat';
import { COST_UNKNOWN_TOOLTIP, PRICE_COST_LABELS, priceCostRelation } from '../service/settlementRules';

const props = defineProps({
  /** 칸 이름 — 예: '월 사용료(VAT 포함)', '120분 초과 통화' */
  label: { type: String, required: true },
  /** 입력 앞 단위 — '10초당' | '건당' | '월' … (비우면 없음) */
  unitText: { type: String, default: '' },
  /** 입력 뒤 단위 — 금액은 '원', 포함 통화는 '분' */
  suffix: { type: String, default: '원' },
  /** 입력 화살표 한 번의 크기(월 사용료는 1,000) */
  step: { type: Number, default: 1 },
  /** 판매가(원). null = 미정(기본 요율) 또는 비움(예외) */
  value: { type: Number, default: null },
  /** 같은 단위의 원가 — 원가 미만·마진 0 판정에 쓴다(원가가 없는 칸은 null) */
  cost: { type: Number, default: null },
  /** 오른쪽 '원가 (참고)' 열의 회색 표기 — 예: '22원/10초'(비우면 '—') */
  costText: { type: String, default: '' },
  /** 원가·단위를 모르는 칸 — 입력을 막는다 */
  locked: { type: Boolean, default: false },
  /** 'default'(기본 요율 모달) | 'override'(회사 예외 모달) */
  mode: {
    type: String,
    default: 'default',
    validator: (v) => v === 'default' || v === 'override',
  },
  /** override: true = 기본 따름, false = 값 입력 */
  follow: { type: Boolean, default: true },
  /** override: "기본 따름" 옆 회색 기본값 — 예: '기본 1분당 200원', '기본 미정' */
  defaultText: { type: String, default: '' },
  /** 필수 표시(*) — 기본 요율에서 비울 수 없는 칸(월 사용료·포함 통화·120분 초과·발신) */
  required: { type: Boolean, default: false },
  /** default: 비운 칸의 placeholder — 기본 '미정'. 미정이 없는 칸(상담사 번호 — 번호 요청 A1)은 '' 로 넘긴다 */
  emptyText: { type: String, default: '미정' },
  /** 행 아래 회색 한 줄 설명(선택) */
  hint: { type: String, default: '' },
});

const emit = defineEmits(['update:value', 'update:follow']);

// <script setup> 본문은 인스턴스마다 도므로 모듈 카운터 대신 인스턴스 uid 로 label ↔ input 을 잇는다
const inputId = `st-rate-price-${getCurrentInstance()?.uid ?? 0}`;
const inputRef = ref(null);

const isOverride = computed(() => props.mode === 'override');
// 원가를 모르는 칸은 값을 넣을 수 없으니 예외 모달에서는 언제나 기본을 따른다
const isFollowing = computed(() => isOverride.value && (props.follow || props.locked));

// 기본을 따르는 동안에는 남아 있는 값이 있어도 비워 보인다
const shownValue = computed(() => (isFollowing.value ? null : props.value));

const placeholder = computed(() => {
  if (!isOverride.value) return props.emptyText;
  return isFollowing.value ? '기본 따름' : '판매가';
});

const relation = computed(() => (isFollowing.value ? null : priceCostRelation(props.value, props.cost)));

const relationLabel = computed(() => {
  if (relation.value === 'below') return PRICE_COST_LABELS.below;
  // 둘 다 0(원가도 판매가도 없음)은 마진을 말할 거리가 아니다
  if (relation.value === 'equal' && props.value !== 0) return PRICE_COST_LABELS.equal;
  return '';
});

const helpText = computed(() => {
  const parts = [];
  if (props.hint) parts.push(props.hint);
  if (isOverride.value && !isFollowing.value && props.value === null) parts.push('비워 두면 기본을 따릅니다');
  return parts.join(' · ');
});

function onChoice(event) {
  const choice = event?.target?.value;
  if (choice === 'follow') {
    emit('update:follow', true);
    // 값만 보는 부모도 같은 뜻이 되게 칸을 비운다
    if (props.value !== null) emit('update:value', null);
  } else if (choice === 'custom' && !props.locked) {
    emit('update:follow', false);
    void nextTick(() => inputRef.value?.focus?.());
  }
}

function onInput(next) {
  const value = typeof next === 'number' && Number.isFinite(next) ? next : null;
  // 기본을 따르는 중에 값을 치면 "값 입력"으로 바꾼다
  if (isOverride.value && props.follow && value !== null) emit('update:follow', false);
  emit('update:value', value);
}
</script>

<style scoped lang="scss">
@use '../styles/settlement' as *;

// 이름 | 입력 | 원가(참고) — 모달의 '원가 (참고)' 열 머리와 같은 폭(110px)
.price-field {
  display: grid;
  grid-template-columns: minmax(110px, 150px) minmax(0, 1fr) 110px;
  gap: 4px 16px;
  align-items: start;
  padding: 10px 0;
  font-size: 13px;

  // 행 사이 구분선은 부모(모달의 묶음)가 긋는다

  &__label {
    margin: 0;
    padding-top: 5px;
    font-size: 13.5px;
    font-weight: 600;
    color: $st-text;
    word-break: keep-all;
  }

  &__required {
    margin-left: 2px;
    font-style: normal;
    color: $st-danger;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }

  &__choice {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
  }

  &__default {
    margin-left: 6px;
    color: $st-muted;
    font-size: 12px;
    @include st-num;
  }

  &__row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 10px;
  }

  &__input {
    display: inline-block;

    // 단위 addon + 입력 + '원' 묶음 전체가 240px 이고 안쪽 입력은 남는 폭을 채운다. 안쪽 입력까지 240px 로
    // 두면 묶음이 칸보다 넓어져 옆의 배지를 덮는다.
    :deep(.ant-input-number-group-wrapper) {
      width: 240px;
      max-width: 100%;
    }

    :deep(.ant-input-number-group-wrapper .ant-input-number) {
      width: 100%;
    }

    :deep(.ant-input-number-input) {
      text-align: right;
      @include st-num;
    }

    // 비활성 입력은 마우스 이벤트를 삼켜 툴팁이 뜨지 않는다 — 감싼 span 이 받게 한다
    &.is-disabled {
      cursor: not-allowed;

      :deep(.ant-input-number-group-wrapper),
      :deep(.ant-input-number) {
        pointer-events: none;
      }
    }
  }

  &__cost {
    padding-top: 5px;
    color: $st-muted;
    font-size: 13px;
    text-align: right;
    @include st-num;
  }

  // "마진 0"(회색) · "원가 미만"(주의) · "원가 확인 중"(점선)
  &__pill {
    @include st-pill;

    &.is-below {
      @include st-pill-tone($st-warning, $st-warning-bg, $st-warning-border);
    }

    &.is-pending {
      @include st-pill-tone($st-warning, #fff, $st-warning-border);
      border-style: dashed;
    }
  }

  &__hint {
    grid-column: 2 / 4;
    margin: 0;
    color: $st-muted;
    font-size: 12.5px;
    line-height: 1.5;
  }

  // 기본을 따르는 동안 입력 칸은 흐리게 — 치면 "값 입력"으로 바뀐다
  &.is-following &__input :deep(.ant-input-number-input) {
    color: $st-muted;
  }

  // 좁은 화면 — 한 칸으로 쌓는다
  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);

    &__cost {
      padding-top: 0;
      text-align: left;
    }

    &__hint {
      grid-column: auto;
    }
  }
}
</style>
